import {
  IonContent,
  IonPage,
  IonButton,
  IonCard,
  IonCardContent,
} from '@ionic/react';
import { useState, useEffect, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { useWallet } from '../contexts/WalletContext';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { playCelebrationSound } from '../utils/soundUtils';
import './BubbleShooter.css';

type Bubble = {
  x: number;
  y: number;
  color: string;
  id: number;
};

type ShotBubble = {
  x: number;
  y: number;
  color: string;
  angle: number;
  speed: number;
};

const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'];
const ROWS = 8;
const COLS = 10;
const BUBBLE_SIZE = 30;
const CANVAS_WIDTH = 400;
const CANVAS_HEIGHT = 600;

const BubbleShooter: React.FC = () => {
  const { earnCoins } = useWallet();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [nextBubble, setNextBubble] = useState<string>(COLORS[0]);
  const [currentBubble, setCurrentBubble] = useState<string>(COLORS[1]);
  const [shotBubble, setShotBubble] = useState<ShotBubble | null>(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [angle, setAngle] = useState(90);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    initializeGame();
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (shotBubble && !gameOver) {
      animateShot();
    }
  }, [shotBubble, gameOver]);

  const initializeGame = () => {
    const newBubbles: Bubble[] = [];
    let id = 0;
    
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        if (Math.random() > 0.3) { // 70% chance of bubble
          newBubbles.push({
            x: col * BUBBLE_SIZE + BUBBLE_SIZE / 2 + (CANVAS_WIDTH - COLS * BUBBLE_SIZE) / 2,
            y: row * BUBBLE_SIZE + BUBBLE_SIZE / 2 + 50,
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
            id: id++,
          });
        }
      }
    }
    
    setBubbles(newBubbles);
    setNextBubble(COLORS[Math.floor(Math.random() * COLORS.length)]);
    setCurrentBubble(COLORS[Math.floor(Math.random() * COLORS.length)]);
    setScore(0);
    setGameOver(false);
    setShotBubble(null);
  };

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw bubbles
    bubbles.forEach(bubble => {
      ctx.beginPath();
      ctx.arc(bubble.x, bubble.y, BUBBLE_SIZE / 2, 0, Math.PI * 2);
      ctx.fillStyle = bubble.color;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    // Draw shot bubble
    if (shotBubble) {
      ctx.beginPath();
      ctx.arc(shotBubble.x, shotBubble.y, BUBBLE_SIZE / 2, 0, Math.PI * 2);
      ctx.fillStyle = shotBubble.color;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Draw cannon and next bubble
    const cannonX = CANVAS_WIDTH / 2;
    const cannonY = CANVAS_HEIGHT - 50;
    
    // Draw cannon
    ctx.save();
    ctx.translate(cannonX, cannonY);
    ctx.rotate((angle - 90) * (Math.PI / 180));
    ctx.fillStyle = '#333';
    ctx.fillRect(-5, -20, 10, 40);
    ctx.restore();

    // Draw current bubble
    ctx.beginPath();
    ctx.arc(cannonX, cannonY, BUBBLE_SIZE / 2, 0, Math.PI * 2);
    ctx.fillStyle = currentBubble;
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw next bubble indicator
    ctx.beginPath();
    ctx.arc(cannonX, cannonY - 40, BUBBLE_SIZE / 2 - 5, 0, Math.PI * 2);
    ctx.fillStyle = nextBubble;
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.stroke();
  }, [bubbles, shotBubble, angle, currentBubble, nextBubble]);

  useEffect(() => {
    draw();
  }, [draw]);

  const animateShot = useCallback(() => {
    if (!shotBubble) return;

    const animate = () => {
      setShotBubble(prev => {
        if (!prev) {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
          return null;
        }

        const newX = prev.x + Math.cos(prev.angle * (Math.PI / 180)) * prev.speed;
        const newY = prev.y + Math.sin(prev.angle * (Math.PI / 180)) * prev.speed;

        // Check wall collisions
        if (newX <= BUBBLE_SIZE / 2 || newX >= CANVAS_WIDTH - BUBBLE_SIZE / 2) {
          const newAngle = 180 - prev.angle;
          animationFrameRef.current = requestAnimationFrame(animate);
          return { ...prev, x: newX, angle: newAngle };
        }

        // Check top collision
        if (newY <= BUBBLE_SIZE / 2) {
          attachBubble(newX, BUBBLE_SIZE / 2);
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
          return null;
        }

        // Check bubble collision
        const collided = bubbles.find(b => {
          const dx = b.x - newX;
          const dy = b.y - newY;
          return Math.sqrt(dx * dx + dy * dy) < BUBBLE_SIZE;
        });

        if (collided) {
          attachBubble(newX, newY);
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
          return null;
        }

        animationFrameRef.current = requestAnimationFrame(animate);
        return { ...prev, x: newX, y: newY };
      });
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [shotBubble, bubbles]);

  const attachBubble = (x: number, y: number) => {
    const newBubble: Bubble = {
      x,
      y,
      color: shotBubble!.color,
      id: Date.now(),
    };

    const newBubbles = [...bubbles, newBubble];
    setBubbles(newBubbles);
    setShotBubble(null);
    
    // Check for matches
    const matches = findMatches(newBubble, newBubbles);
    if (matches.length >= 3) {
      removeMatches(matches, newBubbles);
    } else {
      // Move bubbles down
      moveBubblesDown(newBubbles);
    }

    // Prepare next shot
    setCurrentBubble(nextBubble);
    setNextBubble(COLORS[Math.floor(Math.random() * COLORS.length)]);

    // Check game over
    if (newBubbles.some(b => b.y >= CANVAS_HEIGHT - 100)) {
      handleGameOver();
    }
  };

  const findMatches = (bubble: Bubble, allBubbles: Bubble[]): Bubble[] => {
    const matches: Bubble[] = [bubble];
    const checked = new Set<number>([bubble.id]);

    const checkNeighbors = (b: Bubble) => {
      allBubbles.forEach(nb => {
        if (checked.has(nb.id) || nb.color !== b.color) return;
        
        const dx = nb.x - b.x;
        const dy = nb.y - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < BUBBLE_SIZE * 1.1) {
          matches.push(nb);
          checked.add(nb.id);
          checkNeighbors(nb);
        }
      });
    };

    checkNeighbors(bubble);
    return matches;
  };

  const removeMatches = (matches: Bubble[], allBubbles: Bubble[]) => {
    const matchIds = new Set(matches.map(m => m.id));
    const newBubbles = allBubbles.filter(b => !matchIds.has(b.id));
    setBubbles(newBubbles);
    setScore(prev => prev + matches.length * 10);
    
    if (newBubbles.length === 0) {
      handleWin();
    } else {
      moveBubblesDown(newBubbles);
    }
  };

  const moveBubblesDown = (bubbles: Bubble[]) => {
    setBubbles(bubbles.map(b => ({ ...b, y: b.y + 5 })));
  };

  const shoot = () => {
    if (shotBubble || gameOver) return;

    const cannonX = CANVAS_WIDTH / 2;
    const cannonY = CANVAS_HEIGHT - 50;
    
    setShotBubble({
      x: cannonX,
      y: cannonY,
      color: currentBubble,
      angle,
      speed: 5,
    });
  };

  const handleWin = async () => {
    setGameOver(true);
    playCelebrationSound();
    
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    const coins = 100 + Math.floor(score / 10);
    try {
      await earnCoins(coins, `Bubble Shooter - Score: ${score}`, 'bubble-shooter');
    } catch (error) {
      console.error('Error awarding coins:', error);
    }
  };

  const handleGameOver = async () => {
    setGameOver(true);
    // Only award coins if score is at least 20 (so minimum 31 coins)
    const coins = score >= 20 ? 30 + Math.floor(score / 20) : 0;
    if (coins > 0) {
      try {
        await earnCoins(coins, `Bubble Shooter - Score: ${score}`, 'bubble-shooter');
      } catch (error) {
        console.error('Error awarding coins:', error);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (gameOver) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cannonX = CANVAS_WIDTH / 2;
    const cannonY = CANVAS_HEIGHT - 50;
    
    const dx = x - cannonX;
    const dy = y - cannonY;
    const newAngle = Math.atan2(dy, dx) * (180 / Math.PI);
    setAngle(newAngle);
  };

  return (
    <IonPage>
      <AppHeader title="Bubble Shooter" showBackButton={true} defaultHref="/games" />
      <IonContent fullscreen className="ion-padding">
        <div className="bubble-shooter-container">
          <IonCard className="score-card">
            <IonCardContent>
              <div className="score-display">Score: {score}</div>
            </IonCardContent>
          </IonCard>

          <div className="game-board">
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              onMouseMove={handleMouseMove}
              onClick={shoot}
              className="bubble-canvas"
            />
          </div>

          {gameOver && (
            <IonCard className="game-over-card">
              <IonCardContent>
                <h2>{bubbles.length === 0 ? '🎉 You Win!' : 'Game Over!'}</h2>
                <p>Final Score: {score}</p>
                <IonButton expand="block" color="primary" onClick={initializeGame}>
                  Play Again
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {!gameOver && (
            <div className="game-controls">
              <IonButton expand="block" color="primary" onClick={shoot} disabled={!!shotBubble}>
                Shoot
              </IonButton>
            </div>
          )}

          <IonCard className="info-card">
            <IonCardContent>
              <p><strong>How to Play:</strong></p>
              <p>Aim with your mouse and click to shoot. Match 3+ bubbles of the same color to pop them!</p>
              <p className="coin-info">💰 Coins awarded based on score!</p>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
      <BottomNav />
    </IonPage>
  );
};

export default BubbleShooter;

